<template>
  <Collapse
    class="green-operation__assessment green-operation__waste-and-recycling"
  >
    <template #title>
      <HKLLeftRight>
        <span class="font-bold">{{ $t('tenancy.greenOperation.assessments.wasteAndRecycling.title') }}</span>
        <HKLInputPoints
          v-if="!isEdit && canViewPoint"
          v-model="points"
          :isEdit="false"
        />
      </HKLLeftRight>
    </template>
    <template #content>
      <div>
        <HKLLeftRight class="green-operation__assessment-title">
          <span class="font-bold">
            {{ $t('tenancy.greenOperation.assessments.wasteAndRecycling.foodWasteCollectionCollaboration') }}
          </span>
        </HKLLeftRight>
        <div class="green-operation__assessment-form row">
          <template v-if="isTenant">
            {{ foodWasteCollectionCollaborationHint }}
          </template>
          <template v-else>
            <HKLRadioButton
              v-for="(option, index) of optionMap.foodWasteCollectionCollaboration"
              v-model="foodWasteCollectionCollaboration.value"
              :key="index"
              :disabled="!isEdit"
              :value="option.value"
              class="small-4"
            >
              {{ option.label }}
            </HKLRadioButton>
          </template>
        </div>
      </div>
      <div>
        <HKLLeftRight class="green-operation__assessment-title">
          <span class="font-bold">
            {{ $t('tenancy.greenOperation.assessments.wasteAndRecycling.dedicatedRecyclingFacilitiesIncluded') }}
          </span>
          <HKLInputPoints
            v-if="canViewPoint"
            v-model="dedicatedRecyclingFacilitiesIncluded.point"
            :isEdit="isEdit"
          />
        </HKLLeftRight>
        <div class="green-operation__assessment-form row">
          <template v-if="isTenant">
            {{ dedicatedRecyclingFacilitiesIncludedHint }}
          </template>
          <template v-else>
            <HKLCheckbox
              v-for="(option, index) of optionMap.dedicatedRecyclingFacilitiesIncluded"
              v-model="dedicatedRecyclingFacilitiesIncluded.value[option.key]"
              :key="index"
              :disabled="!isEdit"
              class="small-4"
            >
              {{ option.label }}
            </HKLCheckbox>
          </template>
        </div>
      </div>
      <HKLTable
        v-if="tableAttrs.dataSource.length > 0"
        v-bind="tableAttrs"
      />
    </template>
  </Collapse>
</template>

<script>
import { toRefs, reactive, computed, unref } from 'vue-demi'
import Collapse from '@/controls/Collapse.vue'
import HKLTable from '@/controls/Table/HKLTable.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLRadioButton from '@/controls/Button/HKLRadioButton.vue'
import HKLCheckbox from '@/controls/Button/HKLCheckbox.vue'
import HKLInputPoints from '../components/HKLInputPoints.vue'
import { useStoreGetters } from '@/store/index'
import { useGreenOperationField, useGreenOperationAssessmentFormStatus } from '../hook'
import { GREEN_OPERATION_ASSESSMENT_TYPE } from '../consts'
import { useAssessmentDocumentTableAttrs, useAssessmentForm } from './hook'
import { totalPoints } from './utils'
import { useGreenOperationPerms } from '../GreenOperationLetter.hook'
import i18n from '@/utils/i18n'

const ASSESSMENT_TYPE = GREEN_OPERATION_ASSESSMENT_TYPE.RECYCLING

export default {
  components: {
    Collapse,
    HKLTable,
    HKLLeftRight,
    HKLRadioButton,
    HKLCheckbox,
    HKLInputPoints,
  },
  setup () {
    const sourceId = useGreenOperationField('id', '')
    const { isEdit } = useGreenOperationAssessmentFormStatus()
    const { isTenant } = useStoreGetters('user', ['isTenant'])
    const { canViewPoint } = useGreenOperationPerms()
    const tableAttrs = useAssessmentDocumentTableAttrs(sourceId, ASSESSMENT_TYPE)
    const optionMap = computed(() => ({
      foodWasteCollectionCollaboration: [
        {
          label: i18n.t('action.yes'),
          value: 'yes',
        },
        {
          label: i18n.t('action.no'),
          value: 'no',
        },
      ],
      dedicatedRecyclingFacilitiesIncluded: [
        {
          label: i18n.t('tenancy.greenOperation.assessments.wasteAndRecycling.glass'),
          key: 'glass',
        },
        {
          label: i18n.t('tenancy.greenOperation.assessments.wasteAndRecycling.paper'),
          key: 'paper',
        },
        {
          label: i18n.t('tenancy.greenOperation.assessments.wasteAndRecycling.plastic'),
          key: 'plastic',
        },
        {
          label: i18n.t('tenancy.greenOperation.assessments.wasteAndRecycling.metal'),
          key: 'metal',
        },
        {
          label: i18n.t('tenancy.greenOperation.assessments.wasteAndRecycling.foodWaste'),
          key: 'foodWaste',
        },
      ],
    }))
    const formMap = reactive({
      foodWasteCollectionCollaboration: useAssessmentForm(
        ASSESSMENT_TYPE,
        'foodWasteCollectionCollaboration',
      ),
      foodWasteCollectionCollaborationHint: computed(() => {
        const options = unref(optionMap).foodWasteCollectionCollaboration
        const currentValue = unref(formMap.foodWasteCollectionCollaboration).value
        const option = options.find((option) => option.value === currentValue)
        return option ? option.label : i18n.t('emptyValue')
      }),
      dedicatedRecyclingFacilitiesIncluded: useAssessmentForm(
        ASSESSMENT_TYPE,
        'dedicatedRecyclingFacilitiesIncluded',
        {
          glass: false,
          paper: false,
          plastic: false,
          metal: false,
          foodWaste: false,
        },
      ),
      dedicatedRecyclingFacilitiesIncludedHint: computed(() => {
        const options = unref(optionMap).dedicatedRecyclingFacilitiesIncluded
        const currentValue = unref(formMap.dedicatedRecyclingFacilitiesIncluded).value
        const selectedOptions = options.filter((option) => currentValue[option.key])
        return selectedOptions.length > 0
          ? selectedOptions.map((option) => option.label).join(', ')
          : i18n.t('emptyValue')
      }),
    })
    return {
      isEdit,
      isTenant,
      canViewPoint,
      tableAttrs,
      ...toRefs(formMap),
      optionMap,
      points: computed(() => totalPoints(formMap)),
    }
  },
}
</script>

<style lang="scss" scoped>
@import './common.scss';
</style>
